import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { KnownMapping, ParsedWorksheet, RegionMappingSession } from "@/types";
import { schemaRequiresReportType } from "@/config/reportType";
import {
  compareRegionIdsByNumber,
  getSelectedMappingWorksheets,
  getWorksheetKey,
  isWorksheetSelected,
  resolveMappingRegionId,
} from "@/lib/mappingRegions";

interface UploadState {
  fileUploaded: boolean;
  mappingComplete: boolean;
  file: File | null;
  fileName: string;
  documentId: string;
  schemaId: string;
  schemaName: string;
  providerId: string | null;
  worksheets: ParsedWorksheet[];
  mappingSelection: Record<string, boolean>;
  inputColumns: string[];
  savedMappings: KnownMapping[];
  dqxRunId: number | null;
  dqxJobId: string;
  dqxTriggerMessage: string;
  reportType: string | null;
  /** True when the selected schema requires report type (e.g. Lloyd's). */
  requiresReportType: boolean;
  detectedType: string | null;
  allowedReportTypes: string[];
  isClassifying: boolean;
  /** True when complex ingestion preview has 2+ mapping regions (different schemas). */
  isMultiRegion: boolean;
  mappingRegions: RegionMappingSession[];
  currentRegionIndex: number;
  /** Shown when advancing from one mapping region to the next after save. */
  regionAdvanceNotice: { completedIndex: number; nextIndex: number } | null;
}

interface UploadContextValue extends UploadState {
  confirmUpload: (data: {
    file: File;
    fileName: string;
    documentId: string;
    schemaId: string;
    schemaName: string;
    providerId?: string | null;
    worksheets: ParsedWorksheet[];
    reportType?: string | null;
    regionReportTypes?: Record<string, string | null>;
    mappingSelection?: Record<string, boolean>;
  }) => void;
  resetUpload: () => void;
  saveMappings: (mappings: KnownMapping[]) => void;
  appendRegionMappings: (mappings: KnownMapping[]) => void;
  setDqxRunInfo: (data: { runId: number | null; jobId?: string; message?: string }) => void;
  setClassifying: (isClassifying: boolean) => void;
  setClassificationResult: (data: {
    detectedType: string | null;
    allowedReportTypes: string[];
  }) => void;
  setReportType: (reportType: string | null) => void;
  setRegionReportType: (index: number, reportType: string | null) => void;
  lockRegionAndAdvance: (index: number, mappings: KnownMapping[]) => void;
  setMultiRegionProgress: (data: {
    mappingRegions: RegionMappingSession[];
    currentRegionIndex: number;
  }) => void;
  hydrateMappingProgress: (completedRegionIds: string[]) => void;
  setRegionAdvanceNotice: (notice: { completedIndex: number; nextIndex: number } | null) => void;
  activeRegion: RegionMappingSession | null;
  allRegionsComplete: boolean;
}

const initialState: UploadState = {
  fileUploaded: false,
  mappingComplete: false,
  file: null,
  fileName: "",
  documentId: "",
  schemaId: "",
  schemaName: "",
  providerId: null,
  worksheets: [],
  mappingSelection: {},
  inputColumns: [],
  savedMappings: [],
  dqxRunId: null,
  dqxJobId: "",
  dqxTriggerMessage: "",
  reportType: null,
  requiresReportType: false,
  detectedType: null,
  allowedReportTypes: [],
  isClassifying: false,
  isMultiRegion: false,
  mappingRegions: [],
  currentRegionIndex: 0,
  regionAdvanceNotice: null,
};

function buildMappingRegions(
  worksheets: ParsedWorksheet[],
  selection: Record<string, boolean>,
  regionReportTypes: Record<string, string | null>
): RegionMappingSession[] {
  const regions: RegionMappingSession[] = [];
  worksheets.forEach((ws, index) => {
    if (!isWorksheetSelected(ws, index, selection)) {
      return;
    }
    const regionKey = getWorksheetKey(ws, index);
    const promotedFrom =
      ws.previewCategory === "non_mapping"
        ? ("non_mapping" as const)
        : ws.previewCategory === "ambiguity"
          ? ("ambiguity" as const)
          : undefined;
    regions.push({
      regionId: resolveMappingRegionId(ws),
      worksheetName: ws.name,
      sheetNames: ws.sheetNames?.map((s) => s.trim()).filter(Boolean),
      headers: ws.headers,
      reportType: regionReportTypes[regionKey] ?? null,
      savedMappings: [],
      locked: false,
      ...(promotedFrom
        ? { promotedFrom, sourceRegionId: ws.sourceRegionId, previewKey: ws.previewKey }
        : {}),
    });
  });
  return regions.sort((a, b) => compareRegionIdsByNumber(a.regionId, b.regionId));
}

const UploadContext = createContext<UploadContextValue | null>(null);

export function UploadProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UploadState>(initialState);

  const confirmUpload = useCallback(
    (data: {
      file: File;
      fileName: string;
      documentId: string;
      schemaId: string;
      schemaName: string;
      providerId?: string | null;
      worksheets: ParsedWorksheet[];
      reportType?: string | null;
      regionReportTypes?: Record<string, string | null>;
      mappingSelection?: Record<string, boolean>;
    }) => {
      const selection = data.mappingSelection ?? {};
      const hasComplexSelection = Object.keys(selection).length > 0;
      const mappingWorksheets = hasComplexSelection
        ? getSelectedMappingWorksheets(data.worksheets, selection)
        : data.worksheets.filter(
            (ws) =>
              !ws.isMetadata &&
              ws.previewCategory !== "non_mapping" &&
              ws.previewCategory !== "ambiguity"
          );
      const isMultiRegion = mappingWorksheets.length > 1;
      const regionReportTypes = data.regionReportTypes ?? {};
      const mappingRegions = isMultiRegion
        ? buildMappingRegions(data.worksheets, selection, regionReportTypes)
        : [];
      const singleReportType = isMultiRegion ? null : (data.reportType ?? null);

      const activeHeaders = isMultiRegion
        ? (mappingRegions[0]?.headers ?? [])
        : mappingWorksheets.flatMap((ws) => ws.headers);
      const inputColumns = Array.from(new Set(activeHeaders));

      setState((prev) => ({
        ...prev,
        fileUploaded: true,
        file: data.file,
        fileName: data.fileName,
        documentId: data.documentId,
        schemaId: data.schemaId,
        schemaName: data.schemaName,
        providerId: data.providerId ?? null,
        worksheets: data.worksheets,
        mappingSelection: selection,
        inputColumns,
        savedMappings: [],
        dqxRunId: null,
        dqxJobId: "",
        dqxTriggerMessage: "",
        reportType: singleReportType ?? prev.reportType,
        requiresReportType: schemaRequiresReportType(data.schemaName),
        isMultiRegion,
        mappingRegions,
        currentRegionIndex: 0,
        regionAdvanceNotice: null,
      }));
    },
    []
  );

  const resetUpload = useCallback(() => {
    setState(initialState);
  }, []);

  const saveMappings = useCallback((mappings: KnownMapping[]) => {
    setState((prev) => ({
      ...prev,
      savedMappings: mappings,
      mappingComplete: true,
    }));
  }, []);

  const appendRegionMappings = useCallback((mappings: KnownMapping[]) => {
    setState((prev) => ({
      ...prev,
      savedMappings: [...prev.savedMappings, ...mappings],
    }));
  }, []);

  const setDqxRunInfo = useCallback(
    (data: { runId: number | null; jobId?: string; message?: string }) => {
      setState((prev) => ({
        ...prev,
        dqxRunId: data.runId,
        dqxJobId: data.jobId ?? "",
        dqxTriggerMessage: data.message ?? "",
      }));
    },
    []
  );

  const setClassifying = useCallback((isClassifying: boolean) => {
    setState((prev) =>
      isClassifying
        ? {
            ...prev,
            isClassifying: true,
            // Keep a user/upload-selected reportType so Confirm Structure
            // still shows it while classification refreshes allowed options.
            detectedType: null,
          }
        : { ...prev, isClassifying: false }
    );
  }, []);

  const setClassificationResult = useCallback(
    (data: { detectedType: string | null; allowedReportTypes: string[] }) => {
      setState((prev) => ({
        ...prev,
        detectedType: data.detectedType,
        allowedReportTypes: data.allowedReportTypes,
        reportType:
          prev.reportType !== null
            ? prev.reportType
            : data.detectedType && data.detectedType !== "unknown"
              ? data.detectedType
              : null,
        isClassifying: false,
      }));
    },
    []
  );

  const setReportType = useCallback((reportType: string | null) => {
    setState((prev) => ({ ...prev, reportType }));
  }, []);

  const setRegionReportType = useCallback((index: number, reportType: string | null) => {
    setState((prev) => {
      if (!prev.isMultiRegion || index < 0 || index >= prev.mappingRegions.length) {
        return prev;
      }
      const mappingRegions = prev.mappingRegions.map((region, i) =>
        i === index ? { ...region, reportType } : region
      );
      return {
        ...prev,
        mappingRegions,
        inputColumns: mappingRegions[index]?.headers ?? prev.inputColumns,
      };
    });
  }, []);

  const lockRegionAndAdvance = useCallback((index: number, mappings: KnownMapping[]) => {
    setState((prev) => {
      if (!prev.isMultiRegion || index < 0 || index >= prev.mappingRegions.length) {
        return prev;
      }
      const mappingRegions = prev.mappingRegions.map((region, i) =>
        i === index ? { ...region, locked: true, savedMappings: mappings } : region
      );
      const nextIndex = Math.min(index + 1, mappingRegions.length);
      const allComplete = mappingRegions.every((r) => r.locked);
      const nextRegion = mappingRegions[nextIndex];
      return {
        ...prev,
        mappingRegions,
        currentRegionIndex: allComplete ? index : nextIndex,
        savedMappings: [...prev.savedMappings, ...mappings],
        inputColumns: nextRegion?.headers ?? prev.inputColumns,
        mappingComplete: allComplete,
      };
    });
  }, []);

  const setMultiRegionProgress = useCallback(
    (data: { mappingRegions: RegionMappingSession[]; currentRegionIndex: number }) => {
      const active = data.mappingRegions[data.currentRegionIndex];
      setState((prev) => ({
        ...prev,
        isMultiRegion: data.mappingRegions.length > 1,
        mappingRegions: data.mappingRegions,
        currentRegionIndex: data.currentRegionIndex,
        inputColumns: active?.headers ?? prev.inputColumns,
        savedMappings: data.mappingRegions.flatMap((r) => r.savedMappings),
      }));
    },
    []
  );

  const hydrateMappingProgress = useCallback((completedRegionIds: string[]) => {
    setState((prev) => {
      if (!prev.isMultiRegion || completedRegionIds.length === 0) {
        return prev;
      }
      const mappingRegions = prev.mappingRegions.map((region) => ({
        ...region,
        locked: completedRegionIds.includes(region.regionId),
      }));
      const nextIndex = mappingRegions.findIndex((region) => !region.locked);
      const active = mappingRegions[nextIndex >= 0 ? nextIndex : 0];
      return {
        ...prev,
        mappingRegions,
        currentRegionIndex: nextIndex >= 0 ? nextIndex : Math.max(mappingRegions.length - 1, 0),
        inputColumns: active?.headers ?? prev.inputColumns,
        savedMappings: mappingRegions.flatMap((region) => region.savedMappings),
      };
    });
  }, []);

  const setRegionAdvanceNotice = useCallback(
    (notice: { completedIndex: number; nextIndex: number } | null) => {
      setState((prev) => ({ ...prev, regionAdvanceNotice: notice }));
    },
    []
  );

  const activeRegion =
    state.isMultiRegion && state.mappingRegions.length > 0
      ? (state.mappingRegions[state.currentRegionIndex] ?? null)
      : null;

  const allRegionsComplete =
    state.isMultiRegion &&
    state.mappingRegions.length > 0 &&
    state.mappingRegions.every((r) => r.locked);

  return (
    <UploadContext.Provider
      value={{
        ...state,
        confirmUpload,
        resetUpload,
        saveMappings,
        appendRegionMappings,
        setDqxRunInfo,
        setClassifying,
        setClassificationResult,
        setReportType,
        setRegionReportType,
        lockRegionAndAdvance,
        setMultiRegionProgress,
        hydrateMappingProgress,
        setRegionAdvanceNotice,
        activeRegion,
        allRegionsComplete,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUpload(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) {
    throw new Error("useUpload must be used within <UploadProvider>");
  }
  return ctx;
}
